import React, { useEffect, useState } from 'react';
import { GOLFER_BAGGING_CTAS, GOLFER_BAGGING_CTA_INTERVAL_MS } from '../utils/golfFittingCtas';
import './AnalysisDisplay.css';

interface AnalysisDisplayProps {
  analysis: { bagScore?: number; analysis?: string; response?: string } | null;
  loading: boolean;
}

const renderGenericAnalysis = (text: string) => {
  if (!text) return null;

  const lines = text.split('\n');
  const elements = [];
  let currentList = null;

  for (let i = 0; i < lines.length; i++) {
    const line = lines[i];

    // Headers
    if (line.startsWith('### ')) {
      if (currentList) {
        elements.push(<ul key={`ul-end-${i}`}>{currentList}</ul>);
        currentList = null;
      }
      elements.push(<h4 key={i}>{line.substring(4)}</h4>);
      continue;
    }

    // Sub-headers or bolded paragraphs
    if (line.startsWith('**')) {
        if (currentList) {
            elements.push(<ul key={`ul-end-${i}`}>{currentList}</ul>);
            currentList = null;
        }
        const parts = line.split(/(\*\*.*?\*\*)/g).filter(p => p);
        elements.push(
            <p key={i} className="sub-header">
                {parts.map((part, j) => 
                    part.startsWith('**') && part.endsWith('**')
                        ? <strong key={j}>{part.slice(2, -2)}</strong>
                        : part
                )}
            </p>
        );
        continue;
    }

    // List items
    if (line.trim().startsWith('- ')) {
      if (!currentList) {
        currentList = [];
      }
      const content = line.trim().substring(2);
      const parts = content.split(/(\*\*.*?\*\*)/g).filter(p => p);
      currentList.push(
        <li key={i}>
          {parts.map((part, j) =>
            part.startsWith('**') && part.endsWith('**') ? (
              <strong key={j}>{part.slice(2, -2)}</strong>
            ) : (
              part
            )
          )}
        </li>
      );
      continue;
    }
    
    // End of a list
    if (currentList) {
        elements.push(<ul key={`ul-end-${i}`}>{currentList}</ul>);
        currentList = null;
    }

    // Regular paragraphs
    if (line.trim()) {
      elements.push(<p key={i}>{line}</p>);
    }
  }

  // Final list check
  if (currentList) {
    elements.push(<ul key="ul-final">{currentList}</ul>);
  }

  return elements;
};


const AnalysisDisplay: React.FC<AnalysisDisplayProps> = ({ analysis, loading }) => {
  const [ctaIndex, setCtaIndex] = useState(0);

  useEffect(() => {
    if (!loading) {
      return;
    }

    setCtaIndex(0);

    const interval = setInterval(() => {
      setCtaIndex((prevIndex) => (prevIndex + 1) % GOLFER_BAGGING_CTAS.length);
    }, GOLFER_BAGGING_CTA_INTERVAL_MS);

    return () => clearInterval(interval);
  }, [loading]);

  if (loading) {
    const activeCta = GOLFER_BAGGING_CTAS[ctaIndex];

    return (
      <div className="analysis-container">
        <div className="loading-spinner">
          <div className="spinner" aria-label="Loading bag score analysis" />
          <div className="analysis-cta-list" aria-live="polite">
            <div className="analysis-cta-item is-active">{activeCta}</div>
          </div>
        </div>
      </div>
    );
  }

  // Handle both new format (bagScore + analysis) and legacy format (response)
  const analysisText = analysis?.analysis || analysis?.response;
  const bagScore = analysis?.bagScore;

  if (!analysis || !analysisText) {
    return null;
  }

  return (
    <div className="analysis-container">
      <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', marginBottom: '1rem' }}>
        <h3 className="analysis-title">Bag Analysis</h3>
        {bagScore !== undefined && (
          <div style={{
            backgroundColor: '#e3f2fd',
            color: '#1976d2',
            padding: '0.5rem 1rem',
            borderRadius: '6px',
            fontWeight: 'bold',
            fontSize: '1.1rem'
          }}>
            Score: {bagScore}/100
          </div>
        )}
      </div>
      <div className="analysis-content">
        {renderGenericAnalysis(analysisText)}
      </div>
    </div>
  );
};

export default AnalysisDisplay;
